import React, { useState, useMemo } from "react";
import { useObjectStore } from "../zustand/objects";
import { useShallow } from "zustand/react/shallow";
import { API_EXPORT } from "../config/xano";
import {
  Modal,
  Stack,
  Group,
  Text,
  Button,
  ActionIcon,
  Switch,
  Checkbox,
  Box,
  LoadingOverlay,
  Alert,
} from "@mantine/core";
import {
  IconFileExport,
  IconAlertCircle,
  IconCheck,
  IconChevronLeft,
} from "@tabler/icons-react";
import {
  editorModalShell,
  editorModalStrongTitleStyles,
  editorModalBorder,
  editorModalPrimaryButtonStyles,
  editorModalSecondaryButtonStyles,
  editorModalFootnoteBoxStyle,
} from "../styleFile";
import { zipSync, strToU8 } from "fflate";

const ZIP_GUARDRAILS = {
  HARD_MAX_FILES: 200,
  SOFT_PART_BYTES: 180 * 1024 * 1024,
  HARD_TOTAL_BYTES: 1024 * 1024 * 1024,
  MAX_FILE_BYTES: 100 * 1024 * 1024,
  REQUEST_TIMEOUT_MS: 30000,
  RETRIES_PER_FILE: 1,
};

function bytesToLabel(bytes) {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let idx = 0;
  while (value >= 1024 && idx < units.length - 1) {
    value /= 1024;
    idx += 1;
  }
  return `${value.toFixed(value >= 100 || idx === 0 ? 0 : 1)} ${units[idx]}`;
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function toSafeFilename(value, fallback = "project-export") {
  const base = (value || "").toString().trim();
  const cleaned = base.replace(/[\\/:*?"<>|]+/g, "-").replace(/\s+/g, " ");
  return cleaned.length ? cleaned : fallback;
}

async function fetchWithTimeout(url, options = {}, timeoutMs = 30000) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

function buildAIPrompt(manifest, projectKey) {
  if (!manifest) return "";

  const project = manifest.project || {};
  const storyboard = manifest.storyboard || {};
  const artboards = Array.isArray(manifest.artboards) ? manifest.artboards : [];

  const lines = [
    "You are a 3D scene-building assistant.",
    "Recreate the following project as an interactive multi-scene XR experience.",
    "Keep object intent and interactivity, but choose your own engine-native assets.",
    "",
    `Project key: ${project.project_key || projectKey || "unknown"}`,
    `Project name: ${project.project_name || "Untitled Project"}`,
    `Scenes: ${project.artboard_count ?? artboards.length}`,
    `Coordinate system: ${manifest.coordinate_system?.handedness || "right"}-handed, up ${manifest.coordinate_system?.up || "+Y"}, forward ${manifest.coordinate_system?.forward || "-Z"}`,
    "",
    "General requirements:",
    "- Build one scene per artboard.",
    "- Preserve object names, categories, notes, and destination links.",
    "- Preserve relative layout and orientation from transform values.",
    "- If an asset is unavailable, substitute a close placeholder and keep naming.",
    "",
    "Asset formats (assets.json / manifest.json -> assets.assets[]):",
    "- Each 3D-model asset has TWO file references in the same folder:",
    "    * source_url + asset_path + filename  -> the GLB (compressed, editor-native).",
    "    * obj_source_url + obj_asset_path + obj_filename  -> the OBJ (uncompressed Wavefront,",
    "      generated server-side from the GLB). The obj_* fields are null when no OBJ is available.",
    "- Both files live at the same `assets/<category>/<pose>/` path in the export bundle.",
    "- For engine ingest (Unity, Unreal, runtime three.js loaders without DRACO), prefer the .obj.",
    "- For browser / editor / glTF-aware loaders, prefer the .glb.",
    "- Non-3D assets (figma frames, images) use only source_url / asset_path; obj_* fields are null.",
    "",
    "Scene details:",
  ];

  artboards.forEach((ab) => {
    const sceneId = String(ab.artboard_id ?? "unknown");
    const sceneMeta = storyboard?.[sceneId] || {};
    const objects = Array.isArray(ab.objects) ? ab.objects : [];

    lines.push("");
    lines.push(`Scene ${sceneId}:`);
    lines.push(`- Name: ${sceneMeta.name || `Scene ${sceneId}`}`);
    lines.push(`- Description: ${sceneMeta.description || "None"}`);
    lines.push(`- Comments: ${sceneMeta.comments || "None"}`);
    lines.push(`- Object count: ${ab.object_count ?? objects.length}`);
    lines.push("- Objects:");

    objects.forEach((obj, idx) => {
      const pos = obj?.transform?.position || [0, 0, 0];
      const rot = obj?.transform?.rotation || [0, 0, 0, 1];
      const scale = obj?.transform?.scale || [1, 1, 1];
      const interaction = obj?.destination
        ? `links to scene ${obj.destination}`
        : "no explicit destination";

      lines.push(
        `  ${idx + 1}. ${obj.object_title || obj.model_ref || obj.object_id || "Unnamed object"}`
      );
      lines.push(
        `     - Type/category: ${obj.object_type || "unknown"} / ${obj.category || "unknown"}`
      );
      lines.push(`     - Reference name: ${obj.model_ref || "n/a"}`);
      lines.push(
        `     - Transform: pos(${pos.join(", ")}), rot(${rot.join(", ")}), scale(${scale.join(", ")})`
      );
      lines.push(`     - Visible: ${obj.visible === false ? "false" : "true"}`);
      lines.push(`     - Notes: ${obj.object_notes || "None"}`);
      if (obj.content) lines.push(`     - Text content: ${obj.content}`);
      lines.push(`     - Interactivity: ${interaction}`);
    });
  });

  lines.push("");
  lines.push(
    "Output requirements for the target engine:"
  );
  lines.push("- Create a runnable scene graph with named objects.");
  lines.push("- Implement click/tap interactions for destination-linked objects.");
  lines.push("- Return concise setup notes and any assumptions.");

  return lines.join("\n");
}

function buildExportReadme(manifest, projectKey) {
  const project = manifest?.project || {};
  const projectName = project?.project_name || "Untitled Project";
  const key = project?.project_key || projectKey || "unknown";
  const artboardCount = project?.artboard_count ?? manifest?.artboards?.length ?? "unknown";
  const objectCount = project?.total_objects ?? "unknown";
  const coordinate = manifest?.coordinate_system || {};
  const assetsArr = Array.isArray(manifest?.assets?.assets) ? manifest.assets.assets : [];
  const assetCount = assetsArr.length;
  const objAssetCount = assetsArr.filter((a) => a?.obj_source_url).length;
  const schemaVersion = manifest?.schema_version || "unknown";

  return `# Matchboxxr Export

## Purpose
This package exports a Matchboxxr project for reuse in other tools, runtime importers, and AI-assisted scene reconstruction.

## Project
- Name: ${projectName}
- Key: ${key}
- Artboards: ${artboardCount}
- Objects: ${objectCount}
- Manifest schema version: ${schemaVersion}

## Included Files
- \`manifest.json\` — complete scene and metadata export
- \`assets.json\` — asset index metadata and folder tree
- \`project-prompt.txt\` — AI prompt generated from this project
- \`README.md\` — this guide
- \`assets/<category>/<pose>/\` — per-asset folder containing the \`.glb\` and, when available, the matching \`.obj\` companion

## Coordinate System
- Handedness: ${coordinate?.handedness || "right"}
- Up: ${coordinate?.up || "+Y"}
- Forward: ${coordinate?.forward || "-Z"}
- Units: ${coordinate?.units || "meters"}

## 3D Model Formats (GLB + OBJ)
Every 3D-model asset in this export ships in **two formats** in the same folder:

| Field in \`assets.json\` (and \`manifest.json -> assets.assets[]\`) | Meaning |
| --- | --- |
| \`asset_path\`     | Path of the **GLB** inside the bundle, e.g. \`assets/character/velociraptor/velociraptor-transformed.glb\` |
| \`source_url\`     | Direct URL of the **GLB** in Xano storage |
| \`filename\`       | Filename of the **GLB**, e.g. \`velociraptor-transformed.glb\` |
| \`obj_asset_path\` | Path of the **OBJ** companion inside the bundle, e.g. \`assets/character/velociraptor/velociraptor-transformed.obj\` |
| \`obj_source_url\` | Direct URL of the **OBJ** companion in Xano storage |
| \`obj_filename\`   | Filename of the **OBJ** companion |

The \`obj_*\` fields are \`null\` for non-3D assets (Figma frames, images) and for any 3D model that has not been converted yet.

### Which format should you load?
- **Unity / Unreal / DCC tools / lightweight runtime loaders** — load the **\`.obj\`**. It is plain Wavefront ASCII geometry with no compression, no Draco extension, no glTF dependencies.
- **Web / three.js / glTF-aware viewers / the Matchboxxr editor** — load the **\`.glb\`**. It is smaller and may include Draco compression, materials, textures.
- **AI / scene-reconstruction agents** — the \`.obj\` is easier to parse for geometry-only reasoning; the \`.glb\` retains richer scene data.

The \`assets.json\` \`asset_tree\` map lists every file actually included under \`assets/\` — both formats appear side-by-side in the same folder.

## Asset Notes
- Asset references found: ${assetCount}
- 3D assets with OBJ companion: ${objAssetCount} / ${assetCount}
- If any asset is unavailable, importers should keep object names/transforms and use placeholders.
`;
}

export default function ExportProjectModal({ opened, onClose, projectKey }) {
  const { artboards, storyboardFields } = useObjectStore(
    useShallow((s) => ({
      artboards: s.artboards,
      storyboardFields: s.storyboardFields,
    }))
  );

  const [selectedIds, setSelectedIds] = useState(null);
  const [downloadAssetsBundle, setDownloadAssetsBundle] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [resultMode, setResultMode] = useState("export");
  const [zipProgress, setZipProgress] = useState(null);

  const artboardEntries = useMemo(() => {
    if (!artboards) return [];
    return Object.keys(artboards)
      .sort((a, b) => Number(a) - Number(b))
      .map((id) => {
        const name =
          storyboardFields?.[id]?.name ||
          `Scene ${id}`;
        const count = artboards[id]?.length ?? 0;
        return { id, name, count };
      });
  }, [artboards, storyboardFields]);

  const allIds = useMemo(
    () => artboardEntries.map((ab) => ab.id),
    [artboardEntries]
  );

  const effectiveSelected = selectedIds ?? allIds;
  const allChecked = effectiveSelected.length === allIds.length;
  const someChecked = effectiveSelected.length > 0 && !allChecked;

  const handleToggleAll = () => {
    setSelectedIds(allChecked ? [] : null);
  };

  const handleToggleArtboard = (id) => {
    const current = effectiveSelected;
    const next = current.includes(id)
      ? current.filter((x) => x !== id)
      : [...current, id];
    setSelectedIds(next.length === allIds.length ? null : next);
  };

  const handleExport = async (mode = "export") => {
    setLoading(true);
    setError(null);
    setResult(null);
    setResultMode(mode);

    const body = {
      include_assets: true,
      download_assets_bundle: downloadAssetsBundle,
    };

    if (!allChecked && effectiveSelected.length > 0) {
      body.artboard_ids = effectiveSelected;
    }

    try {
      const requestTs = Date.now();
      const res = await fetch(
        `${API_EXPORT}/export/project/${projectKey}?_ts=${requestTs}`,
        {
          method: "POST",
          headers: {
            "content-type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("user")}`,
          },
          body: JSON.stringify(body),
        }
      );

      if (!res.ok) {
        const errData = await res.json().catch(() => null);
        throw new Error(
          errData?.message || `Export failed (${res.status})`
        );
      }

      const data = await res.json();
      setResult(data);
    } catch (err) {
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError(null);
    setResult(null);
    setResultMode("export");
    onClose();
  };

  const handleResetToOptions = () => {
    setResult(null);
    setError(null);
    setResultMode("export");
    setZipProgress(null);
  };

  const handleClientZipDownload = async () => {
    if (!assetDownloads.length) return;

    const downloadEntries = [];
    for (const asset of assetDownloads) {
      if (asset?.source_url) {
        downloadEntries.push({
          url: asset.source_url,
          path: asset.asset_path || asset.filename || null,
        });
      }
      if (asset?.obj_source_url) {
        downloadEntries.push({
          url: asset.obj_source_url,
          path: asset.obj_asset_path || asset.obj_filename || null,
        });
      }
    }

    if (!downloadEntries.length) return;
    if (downloadEntries.length > ZIP_GUARDRAILS.HARD_MAX_FILES) {
      setError(
        `Too many files to zip in-browser (${downloadEntries.length}). Max is ${ZIP_GUARDRAILS.HARD_MAX_FILES}.`
      );
      return;
    }

    setError(null);
    setZipProgress("Checking file sizes...");

    let knownTotalBytes = 0;
    for (const entry of downloadEntries) {
      try {
        const headRes = await fetchWithTimeout(
          entry.url,
          { method: "HEAD" },
          ZIP_GUARDRAILS.REQUEST_TIMEOUT_MS
        );
        const header = headRes.headers.get("content-length");
        const size = header ? Number(header) : 0;
        if (Number.isFinite(size) && size > 0) {
          knownTotalBytes += size;
        }
      } catch {
        // Size discovery is best-effort; continue.
      }
    }

    if (knownTotalBytes > ZIP_GUARDRAILS.HARD_TOTAL_BYTES) {
      setZipProgress(null);
      setError(
        `Estimated download size ${bytesToLabel(knownTotalBytes)} exceeds browser zip safety cap ${bytesToLabel(
          ZIP_GUARDRAILS.HARD_TOTAL_BYTES
        )}. Use individual downloads.`
      );
      return;
    }

    let partIndex = 1;
    let partBytes = 0;
    let partFiles = {};
    let addedFiles = 0;
    let skippedFiles = 0;
    const partBlobs = [];

    const finalizePart = () => {
      const partFileCount = Object.keys(partFiles).length;
      if (partFileCount === 0) return;
      const zipBytes = zipSync(partFiles, { level: 0 });
      partBlobs.push(new Blob([zipBytes], { type: "application/zip" }));
      partIndex += 1;
      partBytes = 0;
      partFiles = {};
    };

    // Keep manifest context in first part.
    partFiles["manifest.json"] = strToU8(JSON.stringify(manifest, null, 2));
    partFiles["assets.json"] = strToU8(
      JSON.stringify(manifest?.assets || {}, null, 2)
    );
    partFiles["project-prompt.txt"] = strToU8(aiPromptText || "");
    partFiles["README.md"] = strToU8(readmeText || "");

    for (let i = 0; i < downloadEntries.length; i += 1) {
      const entry = downloadEntries[i];
      const sourceUrl = entry.url;

      const entryName =
        entry.path ||
        `assets/file-${String(i + 1).padStart(3, "0")}`;

      setZipProgress(
        `Downloading ${i + 1}/${downloadEntries.length}: ${entryName}`
      );

      let blob = null;
      for (let attempt = 0; attempt <= ZIP_GUARDRAILS.RETRIES_PER_FILE; attempt += 1) {
        try {
          const res = await fetchWithTimeout(
            sourceUrl,
            { method: "GET" },
            ZIP_GUARDRAILS.REQUEST_TIMEOUT_MS
          );
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          blob = await res.blob();
          break;
        } catch {
          if (attempt === ZIP_GUARDRAILS.RETRIES_PER_FILE) {
            blob = null;
          }
        }
      }

      if (!blob) {
        skippedFiles += 1;
        continue;
      }

      if (blob.size > ZIP_GUARDRAILS.MAX_FILE_BYTES) {
        skippedFiles += 1;
        continue;
      }

      if (
        partBytes + blob.size > ZIP_GUARDRAILS.SOFT_PART_BYTES &&
        Object.keys(partFiles).length > 0
      ) {
        setZipProgress(
          `Creating zip part ${partIndex} (${bytesToLabel(partBytes)})...`
        );
        finalizePart();
      }

      const bytes = new Uint8Array(await blob.arrayBuffer());
      partFiles[entryName] = bytes;
      partBytes += blob.size;
      addedFiles += 1;
    }

    setZipProgress(`Creating zip part ${partIndex} (${bytesToLabel(partBytes)})...`);
    finalizePart();
    setZipProgress(null);

    if (partBlobs.length === 1) {
      downloadBlob(partBlobs[0], `${zipBaseName}.zip`);
    } else if (partBlobs.length > 1) {
      partBlobs.forEach((blob, idx) => {
        downloadBlob(
          blob,
          `${zipBaseName}-part-${String(idx + 1).padStart(2, "0")}.zip`
        );
      });
    }

    if (addedFiles === 0) {
      setError("No assets could be added to browser zip. Try individual downloads.");
      return;
    }
    if (skippedFiles > 0) {
      setError(
        `Created zip download with ${addedFiles} file(s). Skipped ${skippedFiles} file(s) due to timeout, size limit, or fetch failure.`
      );
    }
  };

  const canExport = effectiveSelected.length > 0;
  const manifest = result?.manifest || result;
  const packageInfo = result?.package || null;
  const zipUrl = packageInfo?.download_url || null;
  const assetDownloads = Array.isArray(packageInfo?.asset_downloads)
    ? packageInfo.asset_downloads
    : [];
  const aiPromptText = useMemo(
    () => buildAIPrompt(manifest, projectKey),
    [manifest, projectKey]
  );
  const readmeText = useMemo(
    () => buildExportReadme(manifest, projectKey),
    [manifest, projectKey]
  );
  const zipBaseName = toSafeFilename(
    manifest?.project?.project_name,
    projectKey || "project-export"
  );

  const handleDownload = async () => {
    if (zipUrl) {
      const a = document.createElement("a");
      a.href = zipUrl;
      a.download = `${zipBaseName}.zip`;
      a.click();
      downloadBlob(
        new Blob([aiPromptText || ""], { type: "text/plain" }),
        `${zipBaseName}-project-prompt.txt`
      );
      downloadBlob(
        new Blob([readmeText || ""], { type: "text/markdown" }),
        `${zipBaseName}-README.md`
      );
      return;
    }

    if (assetDownloads.length > 0) {
      await handleClientZipDownload();
      return;
    }

    downloadBlob(
      new Blob([JSON.stringify(manifest, null, 2)], {
        type: "application/json",
      }),
      `${projectKey}-scene-description.json`
    );
    downloadBlob(
      new Blob([aiPromptText || ""], { type: "text/plain" }),
      `${zipBaseName}-project-prompt.txt`
    );
    downloadBlob(
      new Blob([readmeText || ""], { type: "text/markdown" }),
      `${zipBaseName}-README.md`
    );
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Export project"
      withCloseButton
      size={480}
      zIndex={300}
      radius={editorModalShell.radius}
      centered={editorModalShell.centered}
      overlayProps={editorModalShell.overlayProps}
      styles={editorModalStrongTitleStyles}
    >
      <LoadingOverlay
        visible={loading}
        loaderProps={{ color: "black" }}
        zIndex={1000}
        overlayProps={{ radius: "sm", blur: 2 }}
      />

      <Stack gap={0}>
        <Stack
          gap="md"
          p={20}
          style={{
            maxHeight: "min(56vh, 440px)",
            overflow: "auto",
          }}
        >
          {result ? (
            <Stack gap="md">
              <Group justify="flex-start">
                <ActionIcon
                  size={26}
                  radius="md"
                  variant="subtle"
                  color="gray"
                  onClick={handleResetToOptions}
                  aria-label="Back to export options"
                >
                  <IconChevronLeft size={16} />
                </ActionIcon>
              </Group>
              <Alert
                icon={<IconCheck size={18} />}
                color="green"
                variant="light"
                title={resultMode === "prompt" ? "Prompt ready" : "Export ready"}
              >
                <Text size="sm">
                  {resultMode === "prompt"
                    ? `Prompt generated from ${manifest?.artboards?.length ?? "all"} artboard(s).`
                    : `Scene description generated with ${manifest?.artboards?.length ?? "all"} artboard(s).`}
                </Text>
              </Alert>

              <Stack gap="xs">
                <Group justify="flex-end" wrap="wrap">
                  {resultMode === "prompt" ? (
                    <>
                      <Button
                        radius={4}
                        styles={editorModalSecondaryButtonStyles}
                        onClick={() =>
                          navigator.clipboard.writeText(aiPromptText)
                        }
                      >
                        Copy AI prompt
                      </Button>
                      <Button
                        radius={4}
                        styles={editorModalPrimaryButtonStyles}
                        onClick={() => {
                          const blob = new Blob([aiPromptText], {
                            type: "text/plain",
                          });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement("a");
                          a.href = url;
                          a.download = `${projectKey}-ai-prompt.txt`;
                          a.click();
                          URL.revokeObjectURL(url);
                        }}
                      >
                        Download AI prompt
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        radius={4}
                        styles={editorModalSecondaryButtonStyles}
                        onClick={() =>
                          navigator.clipboard.writeText(
                            JSON.stringify(manifest, null, 2)
                          )
                        }
                      >
                        Copy project JSON
                      </Button>
                      <Button
                        radius={4}
                        styles={editorModalPrimaryButtonStyles}
                        onClick={handleDownload}
                      >
                        Download
                      </Button>
                    </>
                  )}
                </Group>
                {/* {!zipUrl && assetDownloads.length > 0 && (
                  <Text size="xs" c="dimmed" ta="right">
                    Backend zip is unavailable in this environment. Download uses browser zip assembly.
                  </Text>
                )} */}
                {zipProgress && (
                  <Text size="xs" c="dimmed" ta="right">
                    {zipProgress}
                  </Text>
                )}
              </Stack>
            </Stack>
          ) : (
            <>
              <Box p="md" style={editorModalFootnoteBoxStyle}>
                <Text size="xs" c="dimmed" style={{ lineHeight: 1.55 }}>
                  Export your project as a structured manifest with object details, scene layout and (optionally) asset files.
                </Text>
                <Text size="xs" c="dimmed" style={{ lineHeight: 1.55 }}>
                  Alternatively, generate a prompt describing your scenes for use with generative AI agents.
                </Text>
              </Box>

              <div>
                <Text size="sm" fw={600} c="var(--mbx-modal-primary-text, #111827)" mb={8}>
                  Artboards
                </Text>

                <Stack gap={6}>
                  <Checkbox
                    color="dark"
                    size="sm"
                    label="All artboards"
                    checked={allChecked}
                    indeterminate={someChecked}
                    onChange={handleToggleAll}
                  />

                  <Stack gap={4} ml={24}>
                    {artboardEntries.map((ab) => (
                      <Checkbox
                        key={ab.id}
                        color="dark"
                        size="sm"
                        label={
                          <Text size="sm" component="span">
                            {ab.name}{" "}
                            <Text
                              size="xs"
                              c="dimmed"
                              component="span"
                            >
                              ({ab.count} object
                              {ab.count !== 1 ? "s" : ""})
                            </Text>
                          </Text>
                        }
                        checked={effectiveSelected.includes(ab.id)}
                        onChange={() =>
                          handleToggleArtboard(ab.id)
                        }
                      />
                    ))}
                  </Stack>
                </Stack>
              </div>

              <div>
                <Text size="sm" fw={600} c="var(--mbx-modal-primary-text, #111827)" mb={8}>
                  Options
                </Text>
                <Switch
                  color="dark"
                  size="sm"
                  label="Download asset files"
                  description="Requests packaged asset delivery (zip when available, individual URLs as fallback)"
                  checked={downloadAssetsBundle}
                  onChange={(e) =>
                    setDownloadAssetsBundle(e.currentTarget.checked)
                  }
                  styles={{
                    label: { fontSize: 13 },
                    description: { fontSize: 11 },
                  }}
                />
              </div>

              {error && (
                <Alert
                  icon={<IconAlertCircle size={18} />}
                  color="red"
                  variant="light"
                  title="Export failed"
                >
                  <Text size="sm">{error}</Text>
                </Alert>
              )}
            </>
          )}
        </Stack>

        {!result && (
          <Box
            px={20}
            pb={20}
            pt="lg"
            style={{
              borderTop: `1px solid ${editorModalBorder}`,
            }}
          >
            <Group justify="flex-end">
              <Button
                radius={4}
                styles={editorModalSecondaryButtonStyles}
                disabled={!canExport}
                onClick={() => handleExport("prompt")}
              >
                Generate project prompt
              </Button>
              <Button
                radius={4}
                styles={editorModalPrimaryButtonStyles}
                disabled={!canExport}
                leftSection={<IconFileExport size={16} />}
                onClick={() => handleExport("export")}
              >
                Export
              </Button>
            </Group>
          </Box>
        )}
      </Stack>
    </Modal>
  );
}
